import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  selectDexConfig, 
  selectWallets, 
  selectRpc, 
  selectDex,
  selectTaskConfig,
  selectSwapConfig,
  selectQueueConfig,
  selectOtherConfig,
  selectTokens,
  selectSwapParams,
  selectGasSettings,
  updateConfig,
  updateWallets,
  updateRpc,
  updateDex,
  updateTaskConfig,
  updateSwapConfig,
  updateQueueConfig,
  updateOtherConfig,
  updateTokens,
  addToken,
  removeToken,
  updateSwapParams,
  updatePartialSwapParams,
  updateGasSettings,
  updatePartialGasSettings,
  TaskConfig,
  SwapConfig,
  QueueConfig,
  OtherConfig,
  TokenInfo,
  SwapParams
} from '../store/dexConfigSlice';
import { 
  DexServiceConfig, 
  WalletConfig, 
  RpcConfig, 
  DexConfig,
  GasSettingsConfig 
} from '../types/dex';

/**
 * DEX配置Redux钩子
 * @returns {Object} 配置状态和操作函数
 */
export function useDexConfigRedux() {
  const dispatch = useAppDispatch();
  const config = useAppSelector(selectDexConfig);
  const wallets = useAppSelector(selectWallets);
  const rpc = useAppSelector(selectRpc);
  const dex = useAppSelector(selectDex);
  const task = useAppSelector(selectTaskConfig);
  // 组件使用的交换配置
  const swapConfig = useAppSelector(selectSwapConfig);
  const queueConfig = useAppSelector(selectQueueConfig);
  const otherConfig = useAppSelector(selectOtherConfig);
  const tokens = useAppSelector(selectTokens);
  const swapParams = useAppSelector(selectSwapParams);
  const gasSettings = useAppSelector(selectGasSettings);

  /**
   * 更新整个配置
   * @param {DexServiceConfig} newConfig - 新的完整配置
   */
  const setConfig = useCallback(
    (newConfig: DexServiceConfig) => {
      console.log('更新整个配置:', newConfig);
      dispatch(updateConfig(newConfig));
    },
    [dispatch]
  );

  /**
   * 更新钱包配置
   * @param {WalletConfig[]} newWallets - 新的钱包配置
   */
  const setWallets = useCallback(
    (newWallets: WalletConfig[]) => {
      console.log('更新钱包配置:', newWallets);
      dispatch(updateWallets(newWallets));
    },
    [dispatch]
  );

  /**
   * 更新RPC配置
   * @param {RpcConfig} newRpc - 新的RPC配置
   */
  const setRpc = useCallback(
    (newRpc: RpcConfig) => {
      console.log('更新RPC配置:', newRpc);
      dispatch(updateRpc(newRpc));
    },
    [dispatch]
  );

  /**
   * 更新DEX配置
   * @param {DexConfig} newDex - 新的DEX配置
   */
  const setDex = useCallback(
    (newDex: DexConfig) => {
      console.log('更新DEX配置:', newDex);
      dispatch(updateDex(newDex));
    },
    [dispatch]
  );

  /**
   * 更新任务配置
   * @param {TaskConfig} newTask - 新的任务配置
   */
  const setTask = useCallback(
    (newTask: TaskConfig) => {
      console.log('更新任务配置:', newTask);
      dispatch(updateTaskConfig(newTask));
    },
    [dispatch]
  );

  /**
   * 更新交换配置
   * @param {SwapConfig} newSwapConfig - 新的交换配置
   */
  const setSwapConfig = useCallback(
    (newSwapConfig: SwapConfig) => {
      console.log('更新交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch]
  );

  /**
   * 更新队列配置
   * @param {QueueConfig} newQueueConfig - 新的队列配置
   */
  const setQueueConfig = useCallback(
    (newQueueConfig: QueueConfig) => {
      console.log('更新队列配置:', newQueueConfig);
      dispatch(updateQueueConfig(newQueueConfig));
    },
    [dispatch]
  );

  /**
   * 更新其他配置
   * @param {OtherConfig} newOtherConfig - 新的其他配置
   */
  const setOtherConfig = useCallback(
    (newOtherConfig: OtherConfig) => {
      console.log('更新其他配置:', newOtherConfig);
      dispatch(updateOtherConfig(newOtherConfig));
    },
    [dispatch]
  );

  /**
   * 设置交易的第一个代币
   * @param {TokenInfo | null} token - 代币信息
   */
  const setToken1 = useCallback(
    (token: TokenInfo | null) => {
      console.log('设置token1:', token);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: token, 
            token2: null, 
            slippage: 0.5,
            amountType: 'ALL'
          } 
        : {
            ...swapConfig,
            token1: token
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 设置交易的第二个代币
   * @param {TokenInfo | null} token - 代币信息
   */
  const setToken2 = useCallback(
    (token: TokenInfo | null) => {
      console.log('设置token2:', token);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: token, 
            slippage: 0.5,
            amountType: 'ALL'
          } 
        : {
            ...swapConfig,
            token2: token
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新自定义代币列表
   * @param {TokenInfo[]} newTokens - 新的代币列表
   */
  const setTokens = useCallback(
    (newTokens: TokenInfo[]) => {
      console.log('更新自定义代币列表:', newTokens);
      dispatch(updateTokens(newTokens));
    },
    [dispatch]
  );

  /**
   * 添加单个自定义代币
   * @param {TokenInfo} token - 代币信息
   */
  const addCustomToken = useCallback(
    (token: TokenInfo) => {
      console.log('添加自定义代币:', token);
      dispatch(addToken(token));
    },
    [dispatch]
  );

  /**
   * 删除单个自定义代币
   * @param {string} tokenAddress - 代币地址
   */
  const removeCustomToken = useCallback(
    (tokenAddress: string) => {
      console.log('删除自定义代币:', tokenAddress);
      dispatch(removeToken(tokenAddress));
    },
    [dispatch]
  );

  /**
   * 根据地址获取代币信息
   * @param {string} address - 代币地址
   * @returns {TokenInfo | undefined} 代币信息或undefined
   */
  const getTokenByAddress = useCallback(
    (address: string): TokenInfo | undefined => {
      return tokens.find(
        (token) => token.address.toLowerCase() === address.toLowerCase()
      );
    },
    [tokens]
  );

  /**
   * 将代币1添加到自定义代币列表
   * @param {TokenInfo} token - 代币信息
   */
  const addToken1ToList = useCallback(
    (token: TokenInfo) => {
      // 检查代币是否已存在于列表中
      const exists = tokens.some(
        (t) => t.address.toLowerCase() === token.address.toLowerCase()
      );
      if (!exists) {
        dispatch(addToken(token));
      }
      // 设置为token1
      setToken1(token);
    },
    [dispatch, tokens, setToken1]
  );

  /**
   * 将代币2添加到自定义代币列表
   * @param {TokenInfo} token - 代币信息
   */
  const addToken2ToList = useCallback(
    (token: TokenInfo) => {
      // 检查代币是否已存在于列表中
      const exists = tokens.some(
        (t) => t.address.toLowerCase() === token.address.toLowerCase()
      );
      if (!exists) {
        dispatch(addToken(token));
      }
      // 设置为token2
      setToken2(token);
    },
    [dispatch, tokens, setToken2]
  );

  /**
   * 更新交易参数
   * @param {SwapParams} params - 新的交易参数
   */
  const updateSwapParamsConfig = useCallback(
    (params: SwapParams) => {
      dispatch(updateSwapParams(params));
    },
    [dispatch]
  );

  /**
   * 更新部分交易参数
   * @param {Partial<SwapParams>} params - 部分交易参数
   */
  const updatePartialParams = useCallback(
    (params: Partial<SwapParams>) => {
      dispatch(updatePartialSwapParams(params));
    },
    [dispatch]
  );

  /**
   * 从当前选定的Token初始化交易参数
   * 当用户准备开始交易任务时调用
   */
  const initSwapParamsFromTokens = useCallback(() => {
    if (!swapConfig?.token1 || !swapConfig?.token2) return;

    const partialParams: Partial<SwapParams> = {
      tokenIn: swapConfig.token1.address,
      tokenOut: swapConfig.token2.address,
      tokenInDecimals: swapConfig.token1.decimals,
      tokenOutDecimals: swapConfig.token2.decimals,
      slippage: swapConfig.slippage || 0.5
    };

    dispatch(updatePartialSwapParams(partialParams));
  }, [dispatch, swapConfig]);

  /**
   * 更新Gas设置
   * @param {GasSettingsConfig} newGasSettings - 新的Gas设置
   */
  const updateGasSettingsConfig = useCallback(
    (newGasSettings: GasSettingsConfig) => {
      console.log('更新Gas设置:', newGasSettings);
      dispatch(updateGasSettings(newGasSettings));
    },
    [dispatch]
  );

  /**
   * 更新部分Gas设置
   * @param {Partial<GasSettingsConfig>} partialSettings - 部分Gas设置
   */
  const updatePartialGasSettingsConfig = useCallback(
    (partialSettings: Partial<GasSettingsConfig>) => {
      console.log('更新部分Gas设置:', partialSettings);
      dispatch(updatePartialGasSettings(partialSettings));
    },
    [dispatch]
  );

  /**
   * 更新交易金额类型
   * @param {string} amountType - 新的交易金额类型
   */
  const setAmountType = useCallback(
    (amountType: string) => {
      console.log('更新交易金额类型:', amountType);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType
          } 
        : {
            ...swapConfig,
            amountType
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新最小随机金额
   * @param {string} minAmount - 最小随机金额
   */
  const setMinAmount = useCallback(
    (minAmount: string) => {
      console.log('更新最小随机金额:', minAmount);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'RANDOM',
            minAmount
          } 
        : {
            ...swapConfig,
            minAmount
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新最大随机金额
   * @param {string} maxAmount - 最大随机金额
   */
  const setMaxAmount = useCallback(
    (maxAmount: string) => {
      console.log('更新最大随机金额:', maxAmount);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'RANDOM',
            maxAmount
          } 
        : {
            ...swapConfig,
            maxAmount
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新固定金额
   * @param {string} fixedAmount - 固定金额
   */
  const setFixedAmount = useCallback(
    (fixedAmount: string) => {
      console.log('更新固定金额:', fixedAmount);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'FIXED',
            fixedAmount
          } 
        : {
            ...swapConfig,
            fixedAmount
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新保留金额
   * @param {string} reserveAmount - 保留金额
   */
  const setReserveAmount = useCallback(
    (reserveAmount: string) => {
      console.log('更新保留金额:', reserveAmount);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'RESERVE',
            reserveAmount
          } 
        : {
            ...swapConfig,
            reserveAmount
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新自动卖出状态
   * @param {boolean} autoSell - 是否启用自动卖出
   */
  const setAutoSell = useCallback(
    (autoSell: boolean) => {
      console.log('更新自动卖出状态:', autoSell);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'ALL',
            autoSell
          } 
        : {
            ...swapConfig,
            autoSell
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新卖出金额类型
   * @param {string} sellAmountType - 新的卖出金额类型
   */
  const setSellAmountType = useCallback(
    (sellAmountType: string) => {
      console.log('更新卖出金额类型:', sellAmountType);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'ALL',
            autoSell: true,
            sellAmountType
          } 
        : {
            ...swapConfig,
            sellAmountType
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新卖出最小随机金额
   * @param {string} sellMinAmount - 卖出最小随机金额
   */
  const setSellMinAmount = useCallback(
    (sellMinAmount: string) => {
      console.log('更新卖出最小随机金额:', sellMinAmount);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'ALL',
            autoSell: true,
            sellAmountType: 'RANDOM',
            sellMinAmount
          } 
        : {
            ...swapConfig,
            sellMinAmount
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新卖出最大随机金额
   * @param {string} sellMaxAmount - 卖出最大随机金额
   */
  const setSellMaxAmount = useCallback(
    (sellMaxAmount: string) => {
      console.log('更新卖出最大随机金额:', sellMaxAmount);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'ALL',
            autoSell: true,
            sellAmountType: 'RANDOM',
            sellMaxAmount
          } 
        : {
            ...swapConfig,
            sellMaxAmount
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新卖出固定金额
   * @param {string} sellFixedAmount - 卖出固定金额
   */
  const setSellFixedAmount = useCallback(
    (sellFixedAmount: string) => {
      console.log('更新卖出固定金额:', sellFixedAmount);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'ALL',
            autoSell: true,
            sellAmountType: 'FIXED',
            sellFixedAmount
          } 
        : {
            ...swapConfig,
            sellFixedAmount
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  /**
   * 更新卖出保留金额
   * @param {string} sellReserveAmount - 卖出保留金额
   */
  const setSellReserveAmount = useCallback(
    (sellReserveAmount: string) => {
      console.log('更新卖出保留金额:', sellReserveAmount);
      
      // 如果swapConfig为空，创建一个新的配置对象
      const newSwapConfig = !swapConfig 
        ? { 
            token1: null, 
            token2: null, 
            slippage: 0.5,
            amountType: 'ALL',
            autoSell: true,
            sellAmountType: 'RESERVE',
            sellReserveAmount
          } 
        : {
            ...swapConfig,
            sellReserveAmount
          };
      
      console.log('新的交换配置:', newSwapConfig);
      dispatch(updateSwapConfig(newSwapConfig));
    },
    [dispatch, swapConfig]
  );

  return {
    dexConfig: config,
    wallets,
    rpc,
    dex,
    task,
    swapConfig,
    queueConfig,
    otherConfig,
    tokens,
    swapParams,
    gasSettings,
    setConfig,
    setWallets,
    setRpc,
    setDex,
    setTask,
    setSwapConfig,
    setQueueConfig,
    setOtherConfig,
    addCustomToken,
    removeCustomToken,
    setTokens,
    getTokenByAddress,
    addToken1ToList,
    addToken2ToList,
    setToken1,
    setToken2,
    updateSwapParams: updateSwapParamsConfig,
    updatePartialParams,
    initSwapParamsFromTokens,
    updateGasSettingsConfig,
    updatePartialGasSettingsConfig,
    setAmountType,
    setMinAmount,
    setMaxAmount,
    setFixedAmount,
    setReserveAmount,
    setAutoSell,
    setSellAmountType,
    setSellMinAmount,
    setSellMaxAmount,
    setSellFixedAmount,
    setSellReserveAmount
  };
}
